/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { Component, OnInit } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import {
    GslbApiService,
    GslbBackendModel,
    GslbFrontendModel,
    GslbRealServiceModel,
    GSLBTemplate,
    GslbTopologyModel,
    LbErrorService
} from '../../../shared';
import { GslbBackendViewModel } from '../../../shared/models/gslbBackendView.model';
import { GslbTopologyViewModel } from '../../../shared/models/gslbTopologyViewModel';
import { HealthCheckModel } from '../../../shared/models/healthcheck.model';
import {
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    RbacService
} from '../../../../../core/services';
import { concatMap, takeUntil } from 'rxjs/operators';
import { forkJoin, Observable, Subscriber } from 'rxjs';

export interface GlobalNamesDisplay {
    key: string;
    value: string;
}

export interface MembersDisplay {
    key: string;
    value: string;
}

export interface Display {
    globalNames: GlobalNamesDisplay[];
    members: MembersDisplay[];
}

@Component({
    selector: 'app-gslb',
    templateUrl: './gslb.component.html',
    styleUrls: ['../../../../../core/components/widget/widget.component.scss', './gslb.component.scss']
})

export class GslbComponent extends BaseComponent implements OnInit {
    /**
     * Our available made global names from the API.
     */
    public globalNames: GslbFrontendModel[] = [];

    /**
     * Our available made members from the API.
     */
    public members: GslbRealServiceModel[] = [];

    /**
     * Our available made pools from the API.
     */
    public pools: GslbBackendModel[] = [];

    /**
     * Store the total number of off-line pools
     */
    public poolsOfflineTotal: number = 0;

    /**
     * To store the pools table rows.
     */
    public poolsTableRows: GslbBackendViewModel[] = [];

    /**
     * Our GSLB health check reports/stats.
     */
    public healthCheckReports: HealthCheckModel[] = [];

    /**
     * Our available made topologies from the API.
     */
    public topologies: GslbTopologyModel[] = [];

    /**
     * Our available made topologies from the API.
     */
    public topologiesTableRows: GslbTopologyViewModel[] = [];

    /**
     * Model of how models should be displayed.
     */
    public displayModel: Display = <Display>{};

    /**
     * To store the member names to display in the table.
     */
    public memberNames: any[] = [];

    /**
     * To store the active tab.
     */
    public activeTab: string = '';

    /**
     * Flag to determine if the data has been fetched.
     */
    public dataAvailable: boolean = false;

    /**
     * Store button permissions.
     */
    public permissionsLookup: any = {};

    constructor(
        private activatedRoute: ActivatedRoute,
        private gslbApiService: GslbApiService,
        private languageService: LanguageService,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private router: Router
    ) {
        super();

        this.displayModel = <Display>{};

        this.setupPage();
        this.displayMember();
        this.registerButtons();
    }

    public ngOnInit(): void {
        this.subscribeToQueryParams();
    }

    /**
     * Navigate to supplied URL.
     * @param url
     */
    public navigateToURL(url: string): void {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(url)).then().catch();
    }

    /**
     * Create new GSLB service.
     */
    public createNewService(): void {
        const navigationExtras: NavigationExtras = {
            state: {
                cancelText: 'gslb_return_to_gslb_page',
                parentUrl: '/cluster-configuration/gslb',
                editMode: false,
                duplicate: false,
                // Passing a null value here to make sure it goes straight to the template selector step.
                templates: [GSLBTemplate, null]
            }
        };

        this.router.navigateByUrl(this.navigationService.getEnduranceUrl('/service/add'), navigationExtras).then().catch();
    }

    /**
     * Confirm delete.
     * @param type
     * @param row
     */
    public confirmDelete(type: any, row: any): void {
        const { title, message, action } = this.getFormattedData(type, row);

        const confirmationObject = {
            title: title,
            message: message,
            callBack: this.getBoundApiCall(action, row),
            options: { translateText: false }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Open add or edit page.
     * @param url
     * @param row
     */
    public navigateToAddEdit(url: string, row?: { id: string; }): void {
        if (row) {
            this.navigateToURL(url + '/edit/' + row.id);
        } else {
            this.navigateToURL(url + '/add');
        }
    }

    /**
     * Get the formatted data for submission to the confirmation modal.
     * @param type
     * @param row
     * @private
     */
    private getFormattedData(type: any, row: any): any {
        let title: string;
        let message: string;
        let action: string;
        switch (type) {
            case 'globalName':
                title = 'gslb_delete_global_name';
                message = 'Are you sure you wish to delete global name - ' + row.domain + '?';
                action = 'deleteGlobalName';
                break;
            case 'member':
                title = 'gslb_delete_member';
                message = 'Are you sure you wish to delete member - ' + row.name + '?';
                action = 'deleteMember';
                break;
            case 'pool':
                title = 'gslb_delete_pool';
                message = 'Are you sure you wish to delete pool - ' + row.name + '?';
                action = 'deletePool';
                break;
            case 'topology':
                title = 'gslb_delete_topology';
                message = 'Are you sure you wish to delete topology of subnet - ' + row.subNet.ip + '?';
                action = 'deleteTopology';
                break;
        }

        return { title, message, action };
    }

    /**
     * Update both globalnames and members usage after pools and topologies updates.
     * @private
     */
    private updateGlobalNamesMembersUsage(): void {
        forkJoin({
            globalNames: this.gslbApiService.getFrontends(),
            members: this.gslbApiService.getRealServers()
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (usage: any): void => {
                    if (usage['globalNames']?.length) {
                        this.globalNames = this.formatArray(usage['globalNames'], 'timeToLive');
                    }
                    if (usage['members']?.length) {
                        this.members = this.formatArray(usage['members'], 'weight');
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'GSLBComponent: updateGlobalNamesMembersUsage',
                        'Failed to fetch data.',
                        error
                    );
                }
            });
    }

    /**
     * Set the local variables with the resolver data.
     */
    private setupPage(): void {
        forkJoin({
            hcReport: this.gslbApiService.getHealthChecksReport(),
            globalNames: this.gslbApiService.getFrontends(),
            members: this.gslbApiService.getRealServers(),
            pools: this.gslbApiService.getBackends(),
            topologies: this.gslbApiService.getTopologies(),
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    if (dataGroup['hcReport']) {
                        this.healthCheckReports = dataGroup['hcReport'];
                    }

                    if (dataGroup['globalNames']?.length) {
                        this.globalNames = this.formatArray(dataGroup['globalNames'], 'timeToLive');
                    }

                    if (dataGroup['members']?.length) {
                        this.members = this.formatArray(dataGroup['members'], 'weight');
                    }

                    if (dataGroup['pools']?.length) {
                        this.pools = dataGroup['pools'];
                        this.populatePoolsTableRows();
                    }

                    if (dataGroup['topologies']?.length) {
                        this.topologies = dataGroup['topologies'];
                        this.populateTopologyTableRows();
                    }
                    // Open the pools tab on page load if total offline is > 0
                    if (this.poolsOfflineTotal) {
                        this.activeTab = 'pools';
                    }
                    // Once all data objects have been fetched, display;
                    this.dataAvailable = true;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'GSLBComponent: setupPage',
                        'Failed to fetch data.',
                        error
                    );
                }
            });
    }

    /**
     * Get the api call to call on confirm.
     * @param action
     * @param row
     * @private
     */
    private getBoundApiCall(action: any, row: any): any {
        let apiCall: any;
        switch (action) {
            case 'deleteGlobalName':
                apiCall = this.deleteGlobalName.bind(this, row);
                break;
            case 'deleteMember':
                apiCall = this.deleteMember.bind(this, row);
                break;
            case 'deletePool':
                apiCall = this.deletePool.bind(this, row);
                break;
            case 'deleteTopology':
                apiCall = this.deleteTopology.bind(this, row);
                break;
        }

        return apiCall;
    }

    /**
     * Delete global name.
     * @param row -> Global name object
     * @private
     */
    private deleteGlobalName(row: { id: string; }): Observable<any> {
        return new Observable((observer) => {
            this.gslbApiService.deleteFrontend(row.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.updateGlobalName()
                            .pipe(takeUntil(this.destroyed$))
                            .subscribe(() => {
                                observer.next();
                                observer.complete();
                            });
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('GslbComponent: deleteGlobalName', 'Error submitting GSLB data', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Delete member.
     * @param row -> End point object
     * @private
     */
    private deleteMember(row: { id: string; }): Observable<any> {
        return new Observable((observer) => {
            this.deleteMemberHelper(row)
                .pipe(
                    concatMap(() => this.updateMembers()),
                    takeUntil(this.destroyed$))
                .subscribe({
                    next: (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('GslbComponent: deleteMember', 'Error submitting GSLB data', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Helper for edge-case error object with multiple error messages.
     * @param row
     * @private
     */
    private deleteMemberHelper(row: any): Observable<any> {
        return new Observable((observer) => {
            this.notifyService.muteLocalNotifications();
            this.gslbApiService.deleteRealServer(row.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.notifyService.unmuteLocalNotifications();
                        this.notifyService.success('Member deleted');
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.notifyService.unmuteLocalNotifications();
                        const errorMessage = this.getErrorMessages(error);
                        const notificationId = `gslb-members-${row.id}-delete`;
                        this.notifyService.error(errorMessage, { id: notificationId });
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get error messages if more than one and concatenate into one message, else return single message.
     * NOTE: This is dependant on the message not breaking.
     * @param error
     * @private
     */
    private getErrorMessages(error: ErrorResponseModel): string {
        const errorMessages: any = error.error.errors.map(item => item.message);
        if (errorMessages?.length > 1) {
            const errorMsgPart = errorMessages[1].replace('Can not delete Member as it is linked', ' and');

            return errorMessages[0] + errorMsgPart;
        }

        return errorMessages[0];
    }

    /**
     * Delete pool.
     * @param row -> Pool object
     * @private
     */
    private deletePool(row: { id: string; }): Observable<any> {
        return new Observable((observer) => {
            this.gslbApiService.deleteBackend(row.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.updatePool()
                            .pipe(takeUntil(this.destroyed$))
                            .subscribe(
                                () => {
                                    this.populatePoolsTableRows();
                                    this.updateGlobalNamesMembersUsage();
                                    observer.next();
                                    observer.complete();
                                });
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('GslbComponent: deletePool', 'Error submitting GSLB data', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Delete topology.
     * @param row -> Topology object
     * @private
     */
    private deleteTopology(row: { id: string; }): Observable<any> {
        return new Observable((observer) => {
            this.gslbApiService.deleteTopology(row.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.updateTopologies()
                            .pipe(takeUntil(this.destroyed$))
                            .subscribe(
                                () => {
                                    this.populateTopologyTableRows();
                                    this.updateGlobalNamesMembersUsage();
                                    observer.next();
                                    observer.complete();
                                });
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('GslbComponent: deleteTopology', 'Error submitting GSLB data', error);
                        observer.error(error);
                        observer.complete();
                    }
                }).add((): void => {
                this.loadingService.loadingActionDotsEmitter.next(false);
            });
        });
    }

    /**
     * Get the status of a pool.
     * @param row
     */
    private getPoolStatus(row: GslbBackendModel): string {
        const poolName = row.name;
        for (const i in this.healthCheckReports) {
            if (this.healthCheckReports.hasOwnProperty(i)) {
                if (i === poolName && this.healthCheckReports[i].last_status) {
                    return 'online';
                }

                return 'offline';
            }
        }
    }

    /**
     * Subscribe to inbound search query.
     * @private
     */
    private subscribeToQueryParams(): void {
        this.activatedRoute.queryParams
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (params: any): void => {
                    if (params?.sFilter) {
                        this.activeTab = params.sFilter;
                    }

                    this.navigationService.removeParamsFromUrl();
                }
            });
    }

    /**
     * Format value in given array.
     * @param array
     * @param key
     * @private
     */
    private formatArray(array: GslbFrontendModel[] | GslbRealServiceModel[], key: string): any {
        for (const name of array) {
            name[key] = name[key].toString();
        }

        return array;
    }

    /**
     * Populate  the pool table row.
     * @private
     */
    private populatePoolsTableRows(): void {
        this.poolsTableRows = [];
        let offline = 0;
        for (const pool of this.pools) {
            const status = this.getPoolStatus(pool);
            this.poolsTableRows.push({
                id: pool.id,
                name: pool.name,
                monitor: this.languageService.translate(pool.monitor),
                lbMethod: pool.lbMethod,
                status: status ? status : 'pending'
            });
            if (status === 'offline') {
                offline++;
            }
        }
        this.poolsOfflineTotal = offline;
    }

    /**
     * Populate  the pool table row.
     * @private
     */
    private populateTopologyTableRows(): void {
        this.topologiesTableRows = [];
        for (const topology of this.topologies) {
            const member = this.members.filter(item => item.id === topology.member);
            if (member.length) {
                this.topologiesTableRows.push({
                    id: topology.id,
                    member: member[0].name,
                    region: topology.region,
                    subNet: topology.subNet,
                    subnet: topology.subNet.ip + '/' + topology.subNet.mask
                });
            }
        }
    }

    /**
     * Display the member name.
     * @private
     */
    private displayMember(): void {
        this.memberNames = [];
        if (this.topologies) {
            for (const row of this.topologies) {
                for (const member of this.members) {
                    if (row.member.includes(member.id)) {
                        this.memberNames.push(member.name);
                    }
                }
            }
        }
    }

    /**
     * Update the global name.
     * @private
     */
    private updateGlobalName(): Observable<any> {
        return new Observable((observer) => {
            this.gslbApiService.getFrontends()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        this.globalNames = this.formatArray(data, 'timeToLive');
                    },
                    error: (error: any): void => {
                        this.lbErrorService.pageWithErrors(this.globalNames, error.data.error);
                        this.logService.error('GslbComponent: updateGlobalName', 'Error submitting GSLB data', error);
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Update the members.
     * @private
     */
    private updateMembers(): Observable<any> {
        return new Observable((observer) => {
            this.gslbApiService.getRealServers()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        this.members = this.formatArray(data, 'weight');
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('GslbComponent: updateMembers', 'Error submitting GSLB data', error);
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Update the pool.
     * @private
     */
    private updatePool(): Observable<any> {
        return new Observable((observer) => {
            this.gslbApiService.getBackends()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        this.pools = data;
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('GslbComponent: updatePool', 'Error submitting GSLB data', error);
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Update the topologies.
     * @private
     */
    private updateTopologies(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.gslbApiService.getTopologies()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        this.topologies = data;
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('GslbComponent: updateTopologies', 'Error submitting GSLB data', error);
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'gslb-add-globalname': [{ path: '/gslb/globalnames', methods: ['POST'] }],
            'gslb-edit-globalname': [{ path: '/gslb/globalnames/:id', methods: ['PUT'] }],
            'gslb-delete-globalname': [{ path: '/gslb/globalnames/:id', methods: ['DELETE'] }],
            'gslb-add-member': [{ path: '/gslb/members', methods: ['POST'] }],
            'gslb-edit-member': [{ path: '/gslb/members/:id', methods: ['PUT'] }],
            'gslb-delete-member': [{ path: '/gslb/members/:id', methods: ['DELETE'] }],
            'gslb-add-pool': [{ path: '/gslb/pools', methods: ['POST'] }],
            'gslb-edit-pool': [{ path: '/gslb/pools/:id', methods: ['PUT'] }],
            'gslb-delete-pool': [{ path: '/gslb/pools/:id', methods: ['DELETE'] }],
            'gslb-add-topology': [{ path: '/gslb/topologies', methods: ['POST'] }],
            'gslb-edit-topology': [{ path: '/gslb/topologies/:id', methods: ['PUT'] }],
            'gslb-delete-topology': [{ path: '/gslb/topologies/:id', methods: ['DELETE'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
